import { Check, BellOff, X, Eye, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUpdateAlertState, SNOOZE_OPTIONS, snoozeIso } from '@/hooks/use-alert-lifecycle';
import type { MergedAlert } from '@/hooks/use-merged-alerts';
import { useAgency } from '@/contexts/AgencyContext';

interface Props {
  alert: MergedAlert;
  /** Render as compact icon-only trigger (bell rows) vs full label buttons. */
  compact?: boolean;
}

/**
 * Lifecycle actions for an operational alert. Critical alerts cannot be
 * dismissed without first being resolved.
 */
export function AlertActions({ alert, compact = false }: Props) {
  const { agencyId } = useAgency();
  const update = useUpdateAlertState();

  const apply = (status: 'read' | 'acknowledged' | 'snoozed' | 'resolved' | 'dismissed', snoozedUntil?: string) => {
    update.mutate({
      alertKey: alert.id,
      alertKind: alert.kind,
      brandId: alert.brandId ?? null,
      agencyId: agencyId ?? null,
      status,
      snoozedUntil,
    });
  };

  const canDismiss = !alert.isCritical;

  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
            aria-label="Alert actions"
          >
            <span className="text-base leading-none">⋯</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
          {alert.isUnread && (
            <DropdownMenuItem onClick={() => apply('read')}>
              <Eye className="h-3.5 w-3.5 mr-2" /> Mark as read
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => apply('acknowledged')}>
            <Check className="h-3.5 w-3.5 mr-2" /> Acknowledge
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Clock className="h-3.5 w-3.5 mr-2" /> Snooze
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {SNOOZE_OPTIONS.map(opt => (
                <DropdownMenuItem key={opt.hours} onClick={() => apply('snoozed', snoozeIso(opt.hours))}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => apply('resolved')}>
            <Check className="h-3.5 w-3.5 mr-2" /> Resolve
          </DropdownMenuItem>
          {canDismiss && (
            <DropdownMenuItem onClick={() => apply('dismissed')} className="text-muted-foreground">
              <X className="h-3.5 w-3.5 mr-2" /> Dismiss
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => apply('acknowledged')}>
        <Check className="h-3 w-3" /> Acknowledge
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
            <Clock className="h-3 w-3" /> Snooze
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {SNOOZE_OPTIONS.map(opt => (
            <DropdownMenuItem key={opt.hours} onClick={() => apply('snoozed', snoozeIso(opt.hours))}>
              {opt.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => apply('resolved')}>
        <Check className="h-3 w-3" /> Resolve
      </Button>
      {canDismiss && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs gap-1 text-muted-foreground"
          onClick={() => apply('dismissed')}
        >
          <BellOff className="h-3 w-3" /> Dismiss
        </Button>
      )}
    </div>
  );
}
